import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header, SectionLabel } from "../components/Brand";
import { api, formatIST, BACKEND_URL } from "../lib/api";
import {
  Loader2,
  ArrowLeft,
  Save,
  ExternalLink,
  AlertTriangle,
  FileText,
} from "lucide-react";
import { toast } from "sonner";

const RUBRIC = [
  { key: "business_understanding", label: "Business understanding", max: 15 },
  { key: "buyer_understanding", label: "Buyer understanding", max: 10 },
  { key: "problem_identification", label: "Problem identification", max: 15 },
  { key: "structured_thinking", label: "Structured thinking", max: 15 },
  { key: "practical_recommendations", label: "Practical recommendations", max: 15 },
  { key: "sheet_quality", label: "Google Sheet system quality", max: 15 },
  { key: "communication_clarity", label: "Communication clarity", max: 5 },
  { key: "ownership_initiative", label: "Ownership / initiative", max: 10 },
];

const SHORTLIST_OPTIONS = ["Pending", "Shortlisted", "Rejected", "Maybe"];
const INTERVIEW_OPTIONS = ["Not scheduled", "Scheduled", "Completed"];
const DECISION_OPTIONS = ["Pending", "Selected", "Rejected"];

export default function AdminCandidateDetailPage() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [scoring, setScoring] = useState({
    business_understanding: 0,
    buyer_understanding: 0,
    problem_identification: 0,
    structured_thinking: 0,
    practical_recommendations: 0,
    sheet_quality: 0,
    communication_clarity: 0,
    ownership_initiative: 0,
    internal_notes: "",
    interview_notes: "",
    shortlist_status: "Pending",
    interview_status: "Not scheduled",
    final_decision: "Pending",
  });

  useEffect(() => {
    const t = localStorage.getItem("bh_admin_token");
    if (!t) {
      navigate("/admin");
      return;
    }
    api
      .get(`/admin/candidates/${candidateId}`)
      .then((r) => {
        setC(r.data);
        if (r.data.scoring) {
          setScoring((s) => ({ ...s, ...r.data.scoring }));
        }
      })
      .catch((e) => {
        if (e?.response?.status === 401) navigate("/admin");
        else toast.error("Failed to load candidate");
      })
      .finally(() => setLoading(false));
  }, [candidateId, navigate]);

  const total = useMemo(
    () =>
      RUBRIC.reduce((sum, r) => sum + Math.min(r.max, Math.max(0, Number(scoring[r.key]) || 0)), 0),
    [scoring]
  );

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/admin/candidates/${candidateId}/scoring`, scoring);
      toast.success("Scoring saved");
      setC((prev) => ({ ...prev, scoring: res.data.scoring }));
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !c) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  const sub = c.submission || {};
  const Row = ({ label, value, link }) => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 py-2.5 border-b border-white/5 last:border-b-0">
      <div className="text-[0.6rem] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="sm:col-span-2 text-sm text-zinc-200 break-all">
        {link && value ? (
          <a className="text-white hover:underline inline-flex items-center gap-1" href={value} target="_blank" rel="noreferrer">
            {value}
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        ) : (
          value || "—"
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#09090b] text-white" data-testid="admin-candidate-detail">
      <Header
        right={
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate("/admin/dashboard")}
            className="text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Dashboard
          </button>
        }
      />
      <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <SectionLabel className="mb-2">{c.candidate_code}</SectionLabel>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">
              {c.full_name}
            </h1>
            <p className="text-zinc-500 text-sm mt-2">{c.email} · {c.phone}</p>
          </div>
          {(c.red_flags || []).length > 0 && (
            <div className="border border-red-500/20 bg-red-500/5 rounded-sm p-4 max-w-md">
              <div className="flex items-center gap-2 text-red-400 text-xs uppercase tracking-[0.18em] font-bold mb-2">
                <AlertTriangle className="w-4 h-4" />
                Red Flags ({c.red_flags.length})
              </div>
              <ul className="text-xs text-red-300 space-y-1 list-disc list-inside">
                {c.red_flags.map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Submission */}
          <section className="lg:col-span-3 border border-white/10 rounded-sm p-6">
            <SectionLabel className="mb-4">Submission</SectionLabel>
            <Row label="Status" value={c.submission_status} />
            <Row label="Branch / Year" value={`${c.branch} · ${c.year}`} />
            <Row label="Started" value={formatIST(c.start_at)} />
            <Row label="Deadline" value={formatIST(c.deadline_at)} />
            <Row label="Submitted" value={c.submitted_at ? formatIST(c.submitted_at) : "—"} />
            <Row label="Time taken" value={c.time_taken_seconds ? `${Math.floor(c.time_taken_seconds / 60)} min` : "—"} />
            <Row label="LinkedIn" value={sub.linkedin_url} link />
            <Row label="Portfolio" value={sub.portfolio_url} link />
            <Row label="Google Docs" value={sub.docs_url} link />
            <Row label="Google Sheet" value={sub.sheet_url} link />
            <Row label="Walkthrough / Explanation Video" value={sub.walkthrough_url} link />
            <Row label="AI / tools used" value={sub.ai_tools_used} />
            <Row label="Time spent (self-reported)" value={sub.time_spent} />
            <Row
              label="CV"
              value={
                sub.cv_attached === "yes" && sub.cv_file_path
                  ? sub.cv_filename || "Download"
                  : sub.cv_attached === "no"
                  ? "Will email separately"
                  : "—"
              }
            />
            {sub.cv_attached === "yes" && sub.cv_file_path && (
              <div className="pt-2">
                <a
                  href={`${BACKEND_URL}/api/files/download?path=${encodeURIComponent(sub.cv_file_path)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-xs text-white border border-white/20 px-3 py-2 rounded-sm hover:bg-white/5"
                  data-testid="download-cv-btn"
                >
                  <FileText className="w-3.5 h-3.5" />
                  Download CV
                </a>
              </div>
            )}
          </section>

          {/* Scoring */}
          <section className="lg:col-span-2 border border-white/10 rounded-sm p-6 sticky top-24 self-start">
            <div className="flex items-center justify-between mb-4">
              <SectionLabel>Scoring (/100)</SectionLabel>
              <div className="font-display font-black text-3xl">{total}<span className="text-zinc-600 text-base">/100</span></div>
            </div>
            <div className="space-y-3" data-testid="scoring-rubric">
              {RUBRIC.map((r) => (
                <div key={r.key} className="grid grid-cols-5 items-center gap-3">
                  <label className="col-span-3 text-xs text-zinc-300">
                    {r.label}
                    <span className="text-zinc-600"> /{r.max}</span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={r.max}
                    value={scoring[r.key]}
                    onChange={(e) =>
                      setScoring((s) => ({ ...s, [r.key]: Math.max(0, Math.min(r.max, Number(e.target.value) || 0)) }))
                    }
                    className="bh-input col-span-2 text-center font-mono"
                    data-testid={`score-${r.key}`}
                  />
                </div>
              ))}
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="bh-label">Internal notes</label>
                <textarea
                  data-testid="internal-notes"
                  className="bh-input min-h-[80px] resize-y"
                  value={scoring.internal_notes}
                  onChange={(e) => setScoring((s) => ({ ...s, internal_notes: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="bh-label">Shortlist status</label>
                  <select data-testid="shortlist-status" className="bh-input" value={scoring.shortlist_status} onChange={(e) => setScoring((s) => ({ ...s, shortlist_status: e.target.value }))}>
                    {SHORTLIST_OPTIONS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="bh-label">Interview status</label>
                  <select data-testid="interview-status" className="bh-input" value={scoring.interview_status} onChange={(e) => setScoring((s) => ({ ...s, interview_status: e.target.value }))}>
                    {INTERVIEW_OPTIONS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="bh-label">Interview notes</label>
                  <textarea
                    data-testid="interview-notes"
                    className="bh-input min-h-[60px] resize-y"
                    value={scoring.interview_notes}
                    onChange={(e) => setScoring((s) => ({ ...s, interview_notes: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="bh-label">Final decision</label>
                  <select data-testid="final-decision" className="bh-input" value={scoring.final_decision} onChange={(e) => setScoring((s) => ({ ...s, final_decision: e.target.value }))}>
                    {DECISION_OPTIONS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <button
                data-testid="save-scoring-btn"
                onClick={save}
                disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 bg-white text-black font-bold uppercase tracking-wider px-5 py-3 text-xs hover:bg-zinc-200 rounded-sm disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Evaluation
              </button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
